// utils/helpers.js
// Shared utility functions - DRY principle

import { API_BASE_URL } from '../constants/api';

/**
 * Get the full URL for a product image
 * Handles space-separated strings (API format) and arrays
 * @param {Object} product - Product object with images
 * @param {number} index - Image index to get (default: 0)
 * @returns {string} Full image URL
 */
export const getProductImageUrl = (product, index = 0) => {
  if (!product) return '/placeholder.jpg';

  // Handle images field (can be space-separated string or array)
  if (product.images) {
    const imagesArray = typeof product.images === 'string'
      ? product.images.split(' ').filter(Boolean)
      : product.images;

    if (imagesArray.length > index) {
      const img = imagesArray[index];
      if (img.startsWith('http')) return img;
      return `${API_BASE_URL.replace('/api/v1', '')}${img}`;
    }
  }

  // Fallback to image field
  if (product.image) {
    if (product.image.startsWith('http')) return product.image;
    return `${API_BASE_URL.replace('/api/v1', '')}${product.image}`;
  }

  return '/placeholder.jpg';
};

/**
 * Get all product images as an array of URLs
 * @param {Object} product - Product object with images
 * @returns {string[]} Array of image URLs
 */
export const getAllProductImages = (product) => {
  if (!product?.images) {
    return product?.image ? [getProductImageUrl(product)] : ['/placeholder.jpg'];
  }

  const imagesArray = typeof product.images === 'string'
    ? product.images.split(' ').filter(Boolean)
    : product.images;

  return imagesArray.map(img => {
    if (img.startsWith('http')) return img;
    return `${API_BASE_URL.replace('/api/v1', '')}${img}`;
  });
};

/**
 * Get category image URL
 * @param {Object} category - Category object
 * @param {string} fallback - Fallback image URL
 * @returns {string} Image URL
 */
export const getCategoryImageUrl = (category, fallback = '/placeholder.jpg') => {
  const img = category?.imageUrl || category?.image;
  if (!img) return fallback;
  if (img.startsWith('http')) return img;
  return `${API_BASE_URL.replace('/api/v1', '')}${img}`;
};

/**
 * Format price with currency (uses locale store)
 * @param {number} price - Price value in USD
 * @param {Object} options - Formatting options
 * @returns {string} Formatted price
 */
export const formatPrice = (price, options = {}) => {
  if (price === null || price === undefined) {
    // Import here to avoid circular dependency
    const { useLocaleStore } = require('../stores/localeStore');
    const { getCurrencySymbol } = useLocaleStore.getState();
    return `${getCurrencySymbol()}0.00`;
  }
  
  // Import here to avoid circular dependency
  const { useLocaleStore } = require('../stores/localeStore');
  const { currency, convertPrice } = useLocaleStore.getState();
  
  // Convert price if needed
  const convertedPrice = convertPrice(price);
  
  // Format based on currency
  if (currency === 'CDF') {
    // Format CDF: 1,234,567 FC
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(convertedPrice) + ' FC';
  } else {
    // Format USD: $123.45
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedPrice);
  }
};

/**
 * Calculate discount percentage
 * @param {number} originalPrice - Original price
 * @param {number} currentPrice - Current/sale price
 * @returns {number} Discount percentage
 */
export const calculateDiscount = (originalPrice, currentPrice) => {
  if (!originalPrice || !currentPrice || originalPrice <= currentPrice) return 0;
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
};

/**
 * Format date to locale string
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale (default: en-US)
 * @returns {string} Formatted date
 */
export const formatDate = (date, locale = 'en-US') => {
  if (!date) return '';
  return new Date(date).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/** Date + time for offer/bid list rows */
export const formatDateTime = (date, locale = undefined) => {
  if (!date) return '';
  const resolvedLocale =
    locale ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
  return new Date(date).toLocaleString(resolvedLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Get product price (handles various API formats)
 * @param {Object} product - Product object
 * @returns {{ price: number, comparePrice: number | null }}
 */
export const getProductPrices = (product) => {
  if (product?.priceRange?.min != null) {
    const min = Number(product.priceRange.min) || 0;
    const max = Number(product.priceRange.max) || min;
    return {
      price: min,
      comparePrice: max > min ? max : null,
      fromPrice: true,
    };
  }

  const price = product?.price || product?.newPrice || product?.computedPrice || 0;
  const comparePrice = product?.compareAtPrice || product?.oldPrice || product?.originalPrice || null;
  return { price, comparePrice, fromPrice: false };
};

/**
 * Get product ID (handles _id and id)
 * @param {Object} product - Product object
 * @returns {string}
 */
export const getProductId = (product) => {
  return product?._id || product?.id || '';
};

/** Seller id from product.owner or conversation.seller (populated or raw ObjectId). */
export const getSellerIdFromProduct = (product) => {
  const owner = product?.owner ?? product?.seller;
  if (!owner) return '';
  if (typeof owner === 'string') return owner;
  return owner._id || owner.id || '';
};

export const getUserSellerId = (user) => {
  if (!user?.seller) return '';
  if (typeof user.seller === 'string') return user.seller;
  return user.seller._id || user.seller.id || '';
};

/** True when this seller store belongs to the logged-in user. */
export const isOwnSellerId = (sellerId, user) => {
  if (!sellerId || !user) return false;
  const userSellerId = getUserSellerId(user);
  if (userSellerId && userSellerId === sellerId) return true;
  const userId = user._id || user.id;
  const ownerUserId =
    typeof user.seller === 'object' && user.seller?.user
      ? typeof user.seller.user === 'string'
        ? user.seller.user
        : user.seller.user?._id || user.seller.user?.id
      : '';
  return Boolean(userId && ownerUserId && userId === ownerUserId);
};

/** True when logged-in user owns this product's store (cannot buyer-message own listing). */
export const isOwnStoreProduct = (product, user) => {
  if (!product || !user) return false;
  const sellerId = getSellerIdFromProduct(product);
  if (isOwnSellerId(sellerId, user)) return true;
  const owner = product.owner;
  const userId = user._id || user.id;
  const ownerUserId =
    owner && typeof owner === 'object' && owner.user
      ? typeof owner.user === 'string'
        ? owner.user
        : owner.user?._id || owner.user?.id
      : '';
  return Boolean(userId && ownerUserId && userId === ownerUserId);
};

/** True when user must not act as buyer in this marketplace thread. */
export const isOwnStoreConversation = (conversation, user) => {
  if (!conversation || !user) return false;
  const sellerId =
    conversation.seller?.id ||
    conversation.seller?._id ||
    (typeof conversation.seller === 'string' ? conversation.seller : '');
  if (isOwnSellerId(sellerId, user)) return true;
  const product =
    conversation.contextProduct ||
    conversation.product ||
    conversation.attachedProducts?.[0];
  if (product) {
    return isOwnStoreProduct(
      { ...product, owner: product.owner || conversation.seller },
      user
    );
  }
  return false;
};

/**
 * Get product name (handles name and title)
 * Now supports multilingual fields
 * @param {Object} product - Product object
 * @returns {string}
 */
export const getProductName = (product) => {
  if (!product) return 'Product';
  if (product.name) return product.name;
  if (product.title) return product.title;
  return 'Product';
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text || '';
  return text.substring(0, maxLength).trim() + '...';
};

export {
  canBuyProduct,
  canOfferProduct,
  canDisplayProduct,
  canViewProductPage,
  deriveAvailabilityStatus,
  filterListableProducts,
  findAvailableVariant,
  getAvailabilityBadge,
  getDisplayStock,
  getTotalStock,
  getVariantStock,
  isProductInStock,
  isProductOutOfStock,
  isVariableProductType,
} from './productAvailability';

